<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const handleBackToSettings = () => {
  router.push({
    name: 'aifocus_agents_settings_index',
    params: {
      accountId: route.params.accountId,
      agentId: route.params.agentId,
    },
  });
};

const agentId = computed(() => Number(route.params.agentId));
const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](agentId.value)
);

const uiFlags = useMapGetter('aifocusInboxes/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);

const connectedInboxes = useMapGetter('aifocusInboxes/getRecords');
const allInboxes = useMapGetter('inboxes/getInboxes');

const showConnectModal = ref(false);
const connectingInboxId = ref(null);
const disconnectingInboxId = ref(null);

const availableInboxes = computed(() => {
  const connectedIds = connectedInboxes.value.map(i => i.inbox_id || i.id);
  return allInboxes.value.filter(inbox => !connectedIds.includes(inbox.id));
});

onMounted(() => {
  store.dispatch('aifocusInboxes/get', { agentId: agentId.value });
  store.dispatch('inboxes/get');
  if (!agent.value) {
    store.dispatch('aifocusAgents/show', agentId.value);
  }
});

const handleConnect = async inboxId => {
  try {
    connectingInboxId.value = inboxId;
    await store.dispatch('aifocusInboxes/create', {
      agentId: agentId.value,
      inbox_id: inboxId,
    });
    useAlert('Caixa de entrada conectada com sucesso');
    showConnectModal.value = false;
  } catch (error) {
    useAlert(error?.message || 'Erro ao conectar caixa de entrada');
  } finally {
    connectingInboxId.value = null;
  }
};

const handleDisconnect = async inbox => {
  if (!confirm(`Deseja desconectar "${inbox.name}" deste agente?`)) return;

  try {
    disconnectingInboxId.value = inbox.id;
    await store.dispatch('aifocusInboxes/delete', {
      agentId: agentId.value,
      inboxId: inbox.inbox_id || inbox.id,
    });
    useAlert('Caixa de entrada desconectada');
  } catch (error) {
    useAlert(error?.message || 'Erro ao desconectar');
  } finally {
    disconnectingInboxId.value = null;
  }
};

const getInboxIcon = inbox => {
  const channelType = inbox.channel_type || inbox.channel?.type;
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::Api': 'i-lucide-code',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div class="flex items-center gap-4">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            size="sm"
            @click="handleBackToSettings"
          />
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Caixas de Entrada Conectadas
            </h2>
            <p class="text-sm text-n-slate-11">
              Conecte caixas de entrada para que o agente responda
              automaticamente.
            </p>
          </div>
        </div>
        <Button
          label="Conectar Caixa de Entrada"
          icon="i-lucide-plus"
          @click="showConnectModal = true"
        />
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <!-- Agent Info -->
        <div
          v-if="agent"
          class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-bot text-xl text-n-slate-11" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
              <p class="text-xs text-n-slate-10">
                {{ agent.model || 'Modelo nao configurado' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="!isFetching && connectedInboxes.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div
            class="w-16 h-16 mb-4 rounded-full bg-n-alpha-2 flex items-center justify-center"
          >
            <span class="i-lucide-inbox text-3xl text-n-slate-10" />
          </div>
          <h3 class="text-lg font-medium text-n-slate-12 mb-2">
            Nenhuma caixa de entrada conectada
          </h3>
          <p class="text-sm text-n-slate-11 mb-6 max-w-md">
            Conecte uma caixa de entrada para que o agente possa responder
            mensagens automaticamente.
          </p>
          <Button
            label="Conectar Caixa de Entrada"
            icon="i-lucide-plus"
            @click="showConnectModal = true"
          />
        </div>

        <!-- Connected Inboxes List -->
        <div v-else class="grid gap-3">
          <div
            v-for="inbox in connectedInboxes"
            :key="inbox.id"
            class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
              >
                <span
                  class="text-xl text-n-slate-11"
                  :class="[getInboxIcon(inbox)]"
                />
              </div>
              <div>
                <p class="font-medium text-n-slate-12">{{ inbox.name }}</p>
                <p class="text-xs text-n-slate-10">
                  {{ inbox.channel_type?.replace('Channel::', '') || 'Canal' }}
                </p>
              </div>
            </div>
            <Button
              label="Desconectar"
              variant="secondary"
              size="sm"
              color="ruby"
              :is-loading="disconnectingInboxId === inbox.id"
              @click="handleDisconnect(inbox)"
            />
          </div>
        </div>
      </div>

      <!-- Connect Modal -->
      <div
        v-if="showConnectModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showConnectModal = false"
      >
        <div class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-lg mx-4">
          <div class="px-6 py-4 border-b border-n-weak">
            <h3 class="text-lg font-semibold text-n-slate-12">
              Conectar Caixa de Entrada
            </h3>
            <p class="text-sm text-n-slate-11">
              Selecione uma caixa de entrada para conectar ao agente.
            </p>
          </div>

          <div class="p-6 max-h-96 overflow-y-auto">
            <div v-if="availableInboxes.length === 0" class="text-center py-8">
              <p class="text-n-slate-11">
                Todas as caixas de entrada ja estao conectadas.
              </p>
            </div>

            <div v-else class="grid gap-2">
              <button
                v-for="inbox in availableInboxes"
                :key="inbox.id"
                class="flex items-center gap-3 p-3 rounded-xl border border-n-weak hover:border-n-brand hover:bg-n-alpha-1 transition-colors text-left w-full"
                :disabled="connectingInboxId === inbox.id"
                @click="handleConnect(inbox.id)"
              >
                <div
                  class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
                >
                  <span
                    class="text-xl text-n-slate-11"
                    :class="[getInboxIcon(inbox)]"
                  />
                </div>
                <div class="flex-1">
                  <p class="font-medium text-n-slate-12">{{ inbox.name }}</p>
                  <p class="text-xs text-n-slate-10">
                    {{ inbox.channel_type?.replace('Channel::', '') }}
                  </p>
                </div>
                <span
                  v-if="connectingInboxId === inbox.id"
                  class="i-lucide-loader-2 animate-spin text-n-slate-11"
                />
                <span v-else class="i-lucide-plus text-n-slate-10" />
              </button>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-n-weak flex justify-end">
            <Button
              label="Fechar"
              variant="secondary"
              @click="showConnectModal = false"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
